import type { Components, Theme, ThemeOptions } from "@mui/material/styles";

import {
  borderWidth,
  color,
  effect,
  radius,
  spacing,
} from "@app/theme/weaveTokens.ts";
import { typographyVariant } from "@app/theme/typography.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

type StyleRecord = Record<string, unknown>;
const isRecord = (value: unknown): value is StyleRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Weave's Select rules that DHIG does not have, and that sit in the
 * `variants` array of its MuiSelect slot (arrays cannot be patched key by key
 * when themes merge, so the array is copied with the keys removed):
 *
 * - Outlined: a second focus ring (`#5f60ff 0 0 0 2px`) on the displayed
 *   value. DHIG draws the focus ring once, on the field (see `fieldOverrides`),
 *   so the sidebar hub dropdown showed two nested outlines. The field's ring
 *   stays: only the inner one goes.
 * - Filled: `width: fit-content; minWidth: 0` on the root, which beats a call
 *   site's `minWidth` (the Add collaborator role select shrank from 100px).
 *
 * Reads the array from the merged Weave theme so other variants are copied
 * as they are.
 */
export const weaveSelectVariantFix = (
  weaveComponents: ThemeOptions["components"]
): ThemeOptions["components"] => {
  const root = weaveComponents?.MuiSelect?.styleOverrides?.root;
  const variants = isRecord(root) ? root.variants : undefined;
  if (!Array.isArray(variants)) return {};
  const clone = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(clone);
    if (!isRecord(node)) return node;
    return Object.fromEntries(
      Object.entries(node).map(([key, value]) => [key, clone(value)])
    );
  };
  const patched = variants.map((variant) => {
    const copy = clone(variant) as StyleRecord;
    const variantName = isRecord(copy.props) ? copy.props.variant : undefined;
    const style = isRecord(copy.style) ? copy.style : undefined;
    if (!style) return copy;
    if (variantName === "outlined") {
      const value = style['&& [class*="MuiSelect-select"]'];
      if (isRecord(value) && isRecord(value["&:focus"])) {
        delete value["&:focus"].boxShadow;
      }
    } else if (variantName === "filled") {
      const sizing = style['&[class*="MuiInputBase-root"]'];
      if (isRecord(sizing)) {
        delete sizing.width;
        delete sizing.minWidth;
      }
    }
    return copy;
  });
  return {
    MuiSelect: { styleOverrides: { root: { variants: patched } } },
  } as ThemeOptions["components"];
};

/**
 * Restores the Digital HIG Select: field typography and fill, ellipsised
 * options, and hiding the checked-option icon next to the selected value
 * (Weave's Select theme lacks the `DhigSelect--*` rules, so the checkmark
 * showed under the value).
 */
export const selectOverrides: ThemeComponents = {
  // DHIG's FilledInput override. Weave has none, so a Select inside a
  // `variant="filled"` FormControl (inline table selects) showed MUI's blue
  // underline, a rounded top-left corner and rows about 10px taller.
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: color.surface,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: color.surface,
          boxShadow: "none",
        },
        "&.Mui-focused": {
          backgroundColor: color.surface,
          boxShadow: "none",
        },
        '& + [class*="MuiFormHelperText-root"]': {
          marginLeft: spacing.xs,
        },
        '& [class*="MuiSelect-icon"], [class*="MuiNativeSelect-icon"]': {
          color: color.ink,
          marginRight: spacing.xxs,
          position: "absolute",
          right: 0,
          pointerEvents: "none",
        },
        '&:not(.Mui-disabled) [class*="MuiFilledInput-input"]:before': {
          left: 0,
          right: 0,
          bottom: 0,
          display: "block",
          height: "100%",
          content: "''",
          position: "absolute",
        },
        '&[class*="MuiSelect-root"]:not(.Mui-disabled, .Mui-error):before': {
          borderBottom: `${borderWidth.thin} solid ${color.transparent}`,
          left: spacing.xs,
        },
        '&[class*="MuiSelect-root"]:hover:not(.Mui-disabled, .Mui-error):before':
          {
            borderBottom: `${borderWidth.thin} solid ${color.irisHover}`,
          },
      },
      input: {
        padding: `calc(${spacing.s}/2) ${spacing.xs}`,
        borderBottom: `${borderWidth.thin} solid ${color.transparent}`,
        height: "auto",
        "&:focus, &:focus-visible": {
          borderRadius: `${radius.medium} ${radius.medium} 0 0`,
          boxShadow: effect.focusRingThin,
          backgroundColor: color.surface,
          "&[aria-expanded='true']": { boxShadow: "none" },
        },
        "&[data-active='true']:not(.Mui-error)": {
          borderBottom: `${borderWidth.thick} solid ${color.iris}`,
        },
      },
      underline: {
        borderBottom: 0,
        "&:hover:before": {
          borderBottom: `${borderWidth.thin} solid ${color.irisHover}`,
          left: spacing.xs,
        },
        "&:hover:focus-within:before": { borderBottom: 0 },
        "&:before, &:after, &.Mui-disabled:before, &.Mui-error:before, &:focus:before, &:focus-visible:before, &:active:before":
          { borderBottom: 0 },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        ...typographyVariant["short-copy-medium"],
        color: color.ink,
        backgroundColor: color.fieldFill,
        // Weave's filled variant sets `border: none` on the displayed value,
        // which drops DHIG's 1px transparent bottom border (1px of height).
        // Outlined selects have no such border in DHIG.
        "&[class*='MuiFilledInput-root'] [class*='MuiSelect-select']": {
          borderBottom: `${borderWidth.thin} solid ${color.transparent}`,
        },
        // Weave's filled variant hides the underline `::after`, pads the
        // chevron by 6px and makes it a flex box; DHIG's is a plain block.
        "&&::after": { display: "block" },
        "&& [class*='MuiSelect-icon']": { padding: 0, display: "block" },
        // Weave dims a disabled select to black at 0.4 and blocks pointer
        // events; DHIG keeps MUI's disabled text colour and pointer events.
        "&&[class*='Mui-disabled']": {
          color: color.inkDisabled,
          pointerEvents: "auto",
        },
        "&&[class*='Mui-error']": { boxShadow: effect.fieldError },
        // Weave's outlined variant zeroes the notched outline padding.
        "&& fieldset": { padding: `0 ${spacing.xs}` },
        "& em": {
          color: color.inkMuted,
          fontStyle: "normal",
        },
        "&[aria-expanded='true'] + input + .DhigSvgIcon--type--ui-control-icon":
          { transform: "rotate(180deg)" },
        "& .DhigSelect--option": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "& + .DhigSelect--checkedIcon": { display: "none" },
        },
      },
      select: { minHeight: "inherit" },
      // Weave's chevron is a flex box 0.5rem from the end; MUI's (and DHIG's)
      // is a block 7px from it.
      icon: { display: "block", right: "7px" },
      // DHIG's outlined value keeps 12px on the end; MUI reserves 32px for
      // the chevron, which DHIG's icon placement does not need.
      outlined: { "&&": { paddingRight: spacing.s } },
    },
  },
  // Weave pads the menu paper (12/1/16/1px), rounds it by 4px, adds 2px
  // margins and zeroes the list padding. DHIG's paper is flush with square
  // top corners and its list keeps 8px of padding.
  MuiMenu: {
    styleOverrides: {
      list: {
        paddingTop: spacing.xs,
        paddingBottom: spacing.xs,
      },
      paper: {
        margin: 0,
        marginTop: spacing.xxs,
        padding: 0,
        borderRadius: `${radius.none} ${radius.none} ${radius.medium} ${radius.medium}`,
      },
    },
  },
};
